// React and related imports
import React from 'react';

// React Router
import { useNavigate } from 'react-router-dom';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';

// Chart components
import { MapChart } from '../components/charts/MapChart';
import { QuestionnaireSubmissionTrendChart } from '../components/charts/QuestionnaireSubmissionTrendChart';
import { VerifiedUsersPieChart } from '../components/charts/VerifiedUsersPieChart';
import { AppVersionStatsChart } from '../components/charts/AppVersionStatsChart';
import { WeeklyLogAnalyticsDashboard } from '../components/charts/WeeklyLogAnalyticsDashboard';

// Shared Components
// import { DateFilterPicker } from '../components/charts/shared/DateFilterPicker';

// API hooks
import {
  useFetchOnboardingQuestionnaireStatsQuery,
  useFetchUsersStatsQuery,
  useFetchUsersWeekLogAnalyticsQuery,
} from '../redux/api/statisticsApi';

// Utilities
import { aggregateUsersByCountry } from '../components/charts/utils/aggregateUsersByCountry';

// Shared Components
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { Loading } from '../components/Shared/Loading/Loading';

// Redux hooks and selectors
import { useAppSelector } from '../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../redux/selectors/displaySettingsSelectors';

// UI Components
import { Button } from 'flowbite-react';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  usePageTitle(t('pageTitles.dashboard'));

  // const [filtersDate, setFiltersDate] = React.useState<{
  //   startDate: Date;
  //   endDate: Date;
  // }>({
  //   startDate: new Date(),
  //   endDate: new Date(),
  // });

  const {
    data: usersStats,
    isLoading: isLoadingUsersStats,
    error: errorUsersStats,
  } = useFetchUsersStatsQuery();

  const {
    data: weeklyAnalyticsData,
    isLoading: isLoadingWeeklyAnalytics,
    error: errorWeeklyAnalytics,
  } = useFetchUsersWeekLogAnalyticsQuery({});

  const {
    data: questionnaireStats,
    isLoading: isLoadingQuestionnaireStats,
    error: errorQuestionnaireStats,
  } = useFetchOnboardingQuestionnaireStatsQuery();

  const onboardingQuestionnaireId =
    questionnaireStats?.data?.[0]?.questionnaireId || '';

  // Handler for navigating to questionnaire details
  const handleQuestionnaireDetailsClick = () => {
    if (onboardingQuestionnaireId) {
      navigate(`/questionnaire/${onboardingQuestionnaireId}/analytics`);
    }
  };

  const usersData = [
    {
      name: t('dashboard.platforms.ios'),
      value: usersStats?.data?.iosCount || 0,
    },
    {
      name: t('dashboard.platforms.android'),
      value: usersStats?.data?.androidCount || 0,
    },
  ];

  const usersPerCountry = aggregateUsersByCountry(
    usersStats?.data?.usersPerTimezone || {}
  );

  // Sort appVersionStats by version (descending, latest first)
  function compareVersions(a: string, b: string) {
    const pa = a.split('.').map(Number);
    const pb = b.split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const na = pa[i] || 0;
      const nb = pb[i] || 0;
      if (na > nb) return -1;
      if (na < nb) return 1;
    }
    return 0;
  }

  const sortedAppVersionStats = (usersStats?.data?.appVersionStats || [])
    .slice()
    .sort((a, b) => compareVersions(a.appVersion, b.appVersion));

  return (
    <div className="space-y-6 p-6">
      {/* Row 1: Datepicker */}
      {/**TODO:  Commented out When Backend Implementation is Ready */}
      {/* <div className="w-full">
        <DateFilterPicker
          dateState={filtersDate}
          onChange={(dates) =>
            setFiltersDate({
              startDate:
                typeof dates.startDate === 'string'
                  ? new Date(dates.startDate)
                  : dates.startDate,
              endDate:
                typeof dates.endDate === 'string'
                  ? new Date(dates.endDate)
                  : dates.endDate,
            })
          }
        />
      </div> */}
      {/* Charts Grid */}
      <div className="space-y-6">
        {/* First Row */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-1 lg:grid-cols-2">
          {/* Graph 1: Onboarding Questionnaire Stats Pie Chart */}
          <div className="rounded-2xl bg-white p-4 shadow">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {t('dashboard.charts.onboardingQuestionnaireSubmissionTrend')}
              </h2>
              <Button
                onClick={handleQuestionnaireDetailsClick}
                size="xs"
                className={`${buttonClassName}`}
                disabled={
                  isLoadingQuestionnaireStats || !onboardingQuestionnaireId
                }
              >
                {t('dashboard.buttons.viewDetails')}
              </Button>
            </div>
            {isLoadingQuestionnaireStats ? (
              <div className="flex h-64 items-center justify-center">
                <Loading />
              </div>
            ) : errorQuestionnaireStats || !questionnaireStats?.data ? (
              <div className="flex h-64 items-center justify-center">
                <ErrorComponent errorMessage={t('common.error.loadingData')} />
              </div>
            ) : (
              <QuestionnaireSubmissionTrendChart
                data={questionnaireStats.data}
              />
            )}
          </div>

          {/* Graph 2: Verified Users Pie Chart */}
          <div className="rounded-2xl bg-white p-4 shadow">
            <h2 className="mb-2 text-lg font-semibold text-gray-800">
              {t('dashboard.charts.activeUsers')}
            </h2>
            {isLoadingUsersStats ? (
              <div className="flex h-48 items-center justify-center">
                <Loading />
              </div>
            ) : errorUsersStats || !usersStats?.data ? (
              <div className="flex h-72 items-center justify-center">
                <ErrorComponent errorMessage={t('common.error.loadingData')} />
              </div>
            ) : (
              <VerifiedUsersPieChart data={usersData} />
            )}
          </div>
        </div>

        {/* Second Row: Weekly Log Analytics Dashboard */}
        <div className="rounded-2xl bg-white p-4 shadow">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              {t('analytics.title')}
            </h2>
            <Button
              onClick={() => navigate('/week-log-analytics')}
              size="xs"
              className={buttonClassName}
            >
              {t('dashboard.buttons.viewImpactAnalysis')}
            </Button>
          </div>
          {isLoadingWeeklyAnalytics ? (
            <div className="flex h-64 items-center justify-center">
              <Loading />
            </div>
          ) : errorWeeklyAnalytics || !weeklyAnalyticsData ? (
            <div className="flex h-64 items-center justify-center">
              <ErrorComponent errorMessage={t('common.error.loadingData')} />
            </div>
          ) : (
            <WeeklyLogAnalyticsDashboard
              analyticsData={weeklyAnalyticsData.data}
              isLoading={isLoadingWeeklyAnalytics}
              error={errorWeeklyAnalytics}
            />
          )}
        </div>

        {/* Third Row: App Version Stats Chart */}
        {/* Graph 3: App Version Stats Chart */}
        <div className="rounded-2xl bg-white p-4 shadow">
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            {t('dashboard.charts.appVersionStats')}
          </h2>
          {isLoadingUsersStats ? (
            <div className="flex h-64 items-center justify-center">
              <Loading />
            </div>
          ) : errorUsersStats || !usersStats?.data ? (
            <div className="flex h-64 items-center justify-center">
              <ErrorComponent errorMessage={t('common.error.loadingData')} />
            </div>
          ) : (
            <AppVersionStatsChart data={sortedAppVersionStats} />
          )}
        </div>
      </div>
      {/* Fourth Row: Map Chart (Full Width) */}
      {/* Graph 5: Map Chart */}
      <div className="rounded-2xl bg-white p-4 shadow">
        <h2 className="mb-2 text-lg font-semibold text-gray-800">
          {t('dashboard.charts.usersPerCountry')}
        </h2>
        {isLoadingUsersStats ? (
          <div className="flex h-64 items-center justify-center">
            <Loading />
          </div>
        ) : errorUsersStats || !usersStats?.data ? (
          <div className="flex h-64 items-center justify-center">
            <ErrorComponent errorMessage={t('common.error.loadingData')} />
          </div>
        ) : (
          <MapChart usersByCountry={usersPerCountry} />
        )}
      </div>
    </div>
  );
};
export default HomePage;
